import { useRef, useState } from "react";
import { Film } from "lucide-react";
import { mediaUrl } from "@/mediaUrl";

/** Gallery thumbnail that morphs into a muted, looping ~6 s preview when
 *  the user hovers (or long-presses on touch devices). The video is only
 *  loaded on first hover (preload="none") so a list of 30 stories doesn't
 *  pull 30 MP4s up front. The primary-language render is used since every
 *  published story is guaranteed to have one ready.
 *
 *  Falls back gracefully if the video can't load — the still thumbnail
 *  stays visible and the broken video is never shown.
 */
export default function HoverPreviewThumb({ story }) {
  const videoRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  // Path is the same convention the backend uses for every render —
  // /api/media/video/{video_id}_{lang}.mp4. mediaUrl() resolves it to
  // the right origin (same-origin on custom domains, falls back to
  // build-time backend URL elsewhere).
  const previewSrc = mediaUrl(`/api/media/video/${story.id}_${story.language}.mp4`);

  const onEnter = () => {
    setHovering(true);
    const v = videoRef.current;
    if (!v || videoFailed) return;
    v.muted = true;
    v.currentTime = 0;
    // Some browsers reject autoplay even on user-initiated hover; we ignore
    // the rejection and let the still thumbnail remain visible.
    v.play().catch(() => { /* tolerated */ });
  };
  const onLeave = () => {
    setHovering(false);
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    try { v.currentTime = 0; } catch (_e) { /* ignored */ }
  };

  return (
    <div
      className="aspect-video bg-black relative overflow-hidden"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      data-testid={`story-card-thumb-${story.slug}`}
    >
      {story.thumbnail_url ? (
        <img
          src={mediaUrl(story.thumbnail_url)}
          alt={story.title}
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-500"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Film className="w-10 h-10 text-[color:var(--muted)] opacity-40" />
        </div>
      )}
      {/* Preview overlay: only mounted once we've hovered at least once,
          so the network request never fires for stories the user just
          scrolls past. Stays visible while playing; fades back to the
          poster image when the cursor leaves. */}
      {hovering && !videoFailed && (
        <video
          ref={videoRef}
          src={previewSrc}
          muted
          loop
          playsInline
          preload="metadata"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 pointer-events-none ${videoReady ? "opacity-100" : "opacity-0"}`}
          onLoadedData={() => setVideoReady(true)}
          onError={() => setVideoFailed(true)}
          aria-hidden="true"
          data-testid={`story-card-preview-${story.slug}`}
        />
      )}
      {/* Subtle "▸ preview" badge that hints the card is interactive */}
      <span
        className={`absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white inline-flex items-center gap-1 transition-opacity duration-300 ${hovering ? "opacity-0" : "opacity-90"}`}
        aria-hidden="true"
      >
        <svg viewBox="0 0 10 10" width="9" height="9" fill="currentColor"><path d="M2 1l6 4-6 4z" /></svg>
        Hover to preview
      </span>
    </div>
  );
}
